/**
 * HUIDU Game Service — Frontend API client
 *
 * All HUIDU game operations:
 * - Fetch suppliers (API 5)
 * - Fetch games by supplier (API 6)
 * - Launch game (API 1 SEAMLESS via /game/play proxy)
 */

import { API, DEVELOPMENT } from "../Helper";

// HUIDU API uses the same backend API base
const HUIDU_API_BASE = `${API}/huiduapi`;

const API_BASE = HUIDU_API_BASE;

// ─── Cache helpers ───
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// In-memory cache layer (faster than localStorage for same-session repeated lookups)
const _memCache = new Map();

function setCache(key, data) {
  const ts = Date.now();
  _memCache.set(key, { ts, data });
  try {
    localStorage.setItem(key, JSON.stringify({ ts, data }));
  } catch (e) {
    /* ignore */
  }
}

function getCache(key) {
  // Check in-memory cache first (fastest)
  const mem = _memCache.get(key);
  if (mem) {
    if (Date.now() - mem.ts < CACHE_TTL) return mem.data;
    _memCache.delete(key);
  }
  // Fall back to localStorage
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > CACHE_TTL) {
      localStorage.removeItem(key);
      return null;
    }
    // Populate in-memory cache from localStorage hit
    _memCache.set(key, { ts: parsed.ts, data: parsed.data });
    return parsed.data;
  } catch (e) {
    return null;
  }
}

// ─── Fetch Suppliers (API 5) ───

export async function fetchHuiduSuppliers(useCache = true) {
  const cacheKey = "huidu_suppliers";

  if (useCache) {
    const cached = getCache(cacheKey);
    if (cached) return cached;
  }

  try {
    const response = await fetch(`${API_BASE}/game/providers`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const result = await response.json();

    if (result.success && result.code === 0 && Array.isArray(result.data)) {
      setCache(cacheKey, result.data);
      return result.data;
    }

    return [];
  } catch (error) {
    console.error("[HuiduGameService] fetchSuppliers error:", error);
    return [];
  }
}

// ─── Fetch Games by Supplier (API 6) ───

export async function fetchHuiduGames(supplierCode, useCache = true) {
  const cacheKey = `huidu_games_${supplierCode}`;

  if (useCache) {
    const cached = getCache(cacheKey);
    if (cached) return cached;
  }

  try {
    const url = supplierCode
      ? `${API_BASE}/game/list?code=${encodeURIComponent(supplierCode)}`
      : `${API_BASE}/game/list`;

    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const result = await response.json();

    if (result.success && result.code === 0 && Array.isArray(result.data)) {
      // Normalize game data for frontend use
      const normalized = result.data.map((game) => ({
        game_uid: game.game_uid || game.game_id || "",
        game_name: game.game_name || game.name || "Unknown",
        game_icon: game.game_icon || game.icon || game.image || "",
        image: game.game_icon || game.icon || game.image || "",
        supplier: game.supplier || supplierCode || "",
        game_type: game.game_type || game.type || "slot",
        // Keep original data too
        ...game,
      }));

      setCache(cacheKey, normalized);
      return normalized;
    }

    return [];
  } catch (error) {
    console.error(
      `[HuiduGameService] fetchGames(${supplierCode}) error:`,
      error,
    );
    return [];
  }
}

// ─── Fetch ALL games from all suppliers ───

export async function fetchAllHuiduGames(useCache = true) {
  const cacheKey = "huidu_all_games";

  if (useCache) {
    const cached = getCache(cacheKey);
    if (cached) return cached;
  }

  try {
    // First get all suppliers
    const suppliers = await fetchHuiduSuppliers(useCache);
    if (!suppliers || suppliers.length === 0) return [];

    // Then fetch games for each supplier in parallel
    const gamePromises = suppliers.map((supplier) =>
      fetchHuiduGames(supplier.code, useCache).catch(() => []),
    );

    const gameArrays = await Promise.all(gamePromises);
    const allGames = gameArrays.flat();

    setCache(cacheKey, allGames);
    return allGames;
  } catch (error) {
    console.error("[HuiduGameService] fetchAllGames error:", error);
    return [];
  }
}

// ─── Launch Game (via /game/play proxy) ───

export async function launchHuiduGame(
  gameUid,
  userId,
  creditAmount,
  currencyCode,
  language = "en",
  supplierCode = "",
) {
  try {
    const coins = currencyCode || localStorage.getItem("coin") || "USDT";

    const payload = {
      game_uid: gameUid,
      user_id: String(userId),
      credit_amount: String(creditAmount || "0"),
      currency_code: coins,
      supplier_code: supplierCode || "",
      language: language,
    };

    console.log("[HuiduGameService] Launching game:", payload);

    const response = await fetch(`${API_BASE}/game/play`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (result.success && result.data && result.data.game_launch_url) {
      return {
        success: true,
        game_launch_url: result.data.game_launch_url,
        data: result.data,
      };
    }

    throw new Error(result.error || "Game launch failed");
  } catch (error) {
    console.error("[HuiduGameService] launchGame error:", error);
    throw error;
  }
}

// ─── Launch LuckySport Game (via /game/play/luckysport - forces COP currency) ───

export async function launchLuckySportGame(
  gameUid,
  userId,
  creditAmount,
  currencyCode,
  language = "en",
) {
  try {
    const coins = currencyCode || localStorage.getItem("coin") || "INR";

    const payload = {
      game_uid: gameUid,
      user_id: String(userId),
      credit_amount: String(creditAmount || "0"),
      currency_code: coins,
      language: language,
    };

    console.log("[HuiduGameService] Launching LuckySport game:", payload);

    const response = await fetch(`${API_BASE}/game/play/luckysport`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (result.success && result.data && result.data.game_launch_url) {
      return {
        success: true,
        game_launch_url: result.data.game_launch_url,
        data: result.data,
      };
    }

    throw new Error(result.error || "LuckySport launch failed");
  } catch (error) {
    console.error("[HuiduGameService] launchLuckySportGame error:", error);
    throw error;
  }
}
